import { CardsSection } from 'components/layout/Section/CardsSection';
import { NsKey } from 'enums/NsKey';
import { Sizes } from 'enums/sizes';
import { BorrowerInsuredWithDocumentsForm } from 'features/subscription-process/profiling-process/forms/borrower-insured';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { FileSignature, UserIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import {
  useBorrowerGlobalInfos,
  useBorrowerInsuredCardStatus,
  useBorrowerRiskCardStatus,
  useBorrowerConditionsCardStatus,
  useCanEditBorrowerInsured,
  useCanEditBorrowerRisk,
  useCanEditBorrowerLoan
} from '../../store';
import {
  useBorrowerConditionsCardDisabled,
  useBorrowerRealEstateCardDisabled,
  useBorrowerRealEstateCardStatus,
  useIsBorrowerRealEstateCardVisible
} from '../../store/borrowerSelectors';
import { LightContractCard } from '../cards/LightContractCard';
import { BorrowerRisk } from './BorrowerRisk';
import { BorrowerRealEstate } from './BorrowerRealEstate';
import { useBorrowerRiskData } from 'features/subscription-process/contexts/selectors/selectors';
import { SpecialConditionsForm } from '../../forms/borrower/BorrowerSpecialConditionsForm';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { withPersonName } from 'shared/utils/panelTitle';

interface BorrowerContractInformationsProps {
  borrowerId?: string;
  borrowerProjectId?: string;
}

export const BorrowerContractInformations: FC<
  BorrowerContractInformationsProps
> = ({ borrowerProjectId }) => {
  const globalInfos = useBorrowerGlobalInfos();
  const riskData = useBorrowerRiskData();
  const project = riskData?.project;
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const { openPanel } = usePanel();
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.Common]);

  // Utilisation des selectors centralisés basés sur cardStatuses
  const insuredCardStatus = useBorrowerInsuredCardStatus();
  const riskCardStatus = useBorrowerRiskCardStatus();
  const conditionsCardStatus = useBorrowerConditionsCardStatus();
  const realEstateCardStatus = useBorrowerRealEstateCardStatus();
  const isRealEstateCardVisible = useIsBorrowerRealEstateCardVisible();
  const realEstateLockedByBusinessRule = useBorrowerRealEstateCardDisabled();
  const conditionsLockedByBusinessRule = useBorrowerConditionsCardDisabled();
  const canEditInsured = useCanEditBorrowerInsured();
  const canEditRisk = useCanEditBorrowerRisk();
  const canEditLoan = useCanEditBorrowerLoan();

  const refresh = useCallback(() => {
    refreshSubscriptionProcess();
  }, [refreshSubscriptionProcess]);

  const onInsuredCardClick = useCallback(
    (naturalPersonID: string) => {
      openPanel(
        {
          title: withPersonName(
            t('COMMON.LABELS.EDIT_INSURED_PERSON'),
            globalInfos?.insured?.displayName
          ),
          size: Sizes['2xl']
        },
        <BorrowerInsuredWithDocumentsForm
          personId={naturalPersonID}
          onSuccess={refresh}
          onIdentityStatusChanged={refresh}
          borrowerProjectId={borrowerProjectId}
        />
      );
    },
    [openPanel, refresh, t, borrowerProjectId, globalInfos]
  );

  const onSelectConditions = useCallback(() => {
    if (globalInfos?.quoteId && borrowerProjectId)
      openPanel(
        {
          title: t('CONTRACTUALIZATION.TITLE.MANDATE'),
          size: Sizes['2xl']
        },
        <SpecialConditionsForm
          quoteId={globalInfos.quoteId}
          borrowerProjectId={borrowerProjectId}
          insuranceProjectId={project?.insuranceProjectId}
          personId={globalInfos.insured.id || ''}
          quoteTabs={globalInfos.quoteTabs}
          onSuccess={refresh}
        />
      );
  }, [
    openPanel,
    t,
    globalInfos?.quoteId,
    globalInfos?.insured?.id,
    globalInfos?.quoteTabs,
    borrowerProjectId,
    project?.insuranceProjectId,
    refresh
  ]);

  return (
    <CardsSection title={t('CONTRACTUALIZATION.CARDS.CONTRACT_INFOS_TITLE')}>
      <div className="grid grid-cols-2 gap-4">
        {/* Risk / Prêts du projet : verrouillé dès qu'un emprunteur du projet a envoyé sa demande */}
        <BorrowerRisk status={riskCardStatus} isCardDisabled={!canEditLoan} onRefresh={refreshSubscriptionProcess} />
        {/* Contract person : verrouillé uniquement si cet emprunteur a envoyé sa demande */}
        <LightContractCard
          icon={
            <div
              className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary/10`}
            >
              <UserIcon className="w-5 h-5 text-primary" />
            </div>
          }
          title={globalInfos?.insured?.displayName}
          status={insuredCardStatus}
          disabled={!canEditInsured}
          onClick={() =>
            globalInfos?.insured && globalInfos.insured.id && onInsuredCardClick(globalInfos.insured.id)
          }
        />
        {/* Bien immobilier : visible uniquement pour les projets immobiliers.
            Verrouillé au niveau projet (cardStatuses.realEstate.isDisabled calculé côté back
            depuis isBienLocked = isPretsLocked) dès qu'un emprunteur a envoyé sa DIA. */}
        {isRealEstateCardVisible && (
          <BorrowerRealEstate
            status={realEstateCardStatus}
            isCardDisabled={realEstateLockedByBusinessRule}
            borrowerProjectId={borrowerProjectId}
            onRefresh={refreshSubscriptionProcess}
          />
        )}
        {/* Contract conditions : verrouillé uniquement si cet emprunteur a envoyé sa demande */}
        <LightContractCard
          className={isRealEstateCardVisible ? undefined : 'col-span-2'}
          icon={
            <div
              className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary/10`}
            >
              <FileSignature className="w-5 h-5 text-primary" />
            </div>
          }
          title={'CONTRACTUALIZATION.CARDS.UNEMPLOYMENT_CONDITIONS_TITLE'}
          subtitle={'CONTRACTUALIZATION.CARDS.UNEMPLOYMENT_CONDITIONS_SUBTITLE'}
          status={conditionsCardStatus}
          disabled={!canEditRisk || conditionsLockedByBusinessRule}
          onClick={() => onSelectConditions()}
        />
      </div>
    </CardsSection>
  );
};
